import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getChannel, setAiEnabled, timeLimitStatus, updateAssignmentSettings, updateFirmDetails, updateMoneySettings } from '../lib/api';
import { friendlyError } from '../lib/format';
import { Button, ErrorText, Notice, PageHeader, SelectField, TextField } from '../components/ui';
import type { AssignRules, WhatsAppChannel } from '../lib/types';

export function Settings() {
  const { firm, isManager, reload } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const on = !!firm?.ai_enabled;
  return (
    <div>
      <PageHeader title="Settings" />
      <FirmDetails />
      <WhatsAppConnection />
      <section className="max-w-[640px] rounded-2xl border border-line bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg">AI desk</h2>
            <p className="text-sm text-muted">{on
              ? 'On. AI can draft replies from approved project facts. Live WhatsApp replies require a connected Meta number; unsupported numbers wait in Review and risky claims are blocked.'
              : 'Off. Buyer messages wait for your team. Nothing is sent automatically.'}</p>
          </div>
          {isManager && (
            <Button variant={on ? 'danger' : 'primary'} disabled={busy} onClick={async () => {
              setBusy(true); setError('');
              try { await setAiEnabled(!on); await reload(); } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
            }}>{on ? 'Turn off' : 'Turn on'}</Button>
          )}
        </div>
        <div className="mt-2"><ErrorText>{error}</ErrorText></div>
      </section>
      <AssignmentSettings />
      <MoneySettings />
    </div>
  );
}

function WhatsAppConnection() {
  const [channel, setChannel] = useState<WhatsAppChannel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    getChannel().then(setChannel).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
  }, []);
  return <section className="mb-5 max-w-[640px] rounded-2xl border border-line bg-white p-5">
    <div className="flex items-center justify-between gap-3"><h2 className="text-lg font-extrabold">WhatsApp connection</h2>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${channel ? 'bg-okSoft text-ok' : 'bg-warnSoft text-warn'}`}>{loading ? 'Checking…' : channel ? 'Number linked' : 'Simulator only'}</span>
    </div>
    <p className="mt-2 text-sm leading-relaxed text-muted">{channel
      ? `This workspace has ${channel.display_number} linked. To confirm live delivery, test an inbound message and an approved reply.`
      : 'Your team can test the complete lead journey in the built-in simulator. Live WhatsApp needs a Meta Business number, webhook verification and server-side credentials.'}</p>
    {error && <div className="mt-3"><ErrorText>{error}</ErrorText></div>}
    <p className="mt-3 text-xs leading-relaxed text-muted">Connection is completed securely by the ReAstra operator after the business number is verified. Never paste Meta access tokens into a project, brochure or chat.</p>
    <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline">Open Meta Developer Dashboard ↗</a>
  </section>;
}

const RULES: Array<[keyof AssignRules, string, string]> = [
  ['returning', 'Returning buyer', 'Goes back to the rep who handled them before'],
  ['sourced', 'Sourced by', "A lead from a rep's own link or contact goes to that rep"],
  ['nri', 'NRI desk', 'Buyers with a foreign number go to the NRI team'],
  ['project_team', 'Project team', "Goes to the team that handles the project (set the team on each project's page)"],
  ['language', 'Language', "Prefers a rep who speaks the buyer's language (set languages on the Team page)"],
];

function AssignmentSettings() {
  const { firm, teams, isManager, reload } = useAuth();
  const [auto, setAuto] = useState(firm?.auto_assign ?? true);
  const [rules, setRules] = useState<AssignRules>(firm?.assign_rules ?? { returning: true, sourced: true, nri: true, project_team: true, language: true });
  const [cap, setCap] = useState(String(firm?.open_lead_cap ?? 25));
  const [hot, setHot] = useState(String(firm?.hot_minutes ?? 15));
  const [warm, setWarm] = useState(String(firm?.warm_minutes ?? 120));
  const [nri, setNri] = useState(firm?.nri_team_id ?? '');
  const [status, setStatus] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => { timeLimitStatus().then((s) => setStatus(s.scheduled)).catch(() => setStatus(null)); }, []);

  async function save() {
    setBusy(true); setError(''); setSaved(false);
    try {
      await updateAssignmentSettings({ autoAssign: auto, rules, cap: Number(cap), hot: Number(hot), warm: Number(warm), nriTeamId: nri || null });
      await reload(); setSaved(true);
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  }

  return (
    <section className="mt-5 max-w-[640px] rounded-2xl border border-line bg-surface p-5">
      <h2 className="font-display text-lg">Lead assignment</h2>
      <p className={`mb-4 text-sm ${status === false ? 'text-bad' : 'text-muted'}`}>
        {status === null ? 'Checking the time-limit checker…' : status ? 'Time-limit checker is running every minute.' : 'Time-limit checker is NOT scheduled. Missed time limits will not move leads. Tell your ReAstra contact.'}
      </p>
      <label className="mb-3 flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={auto} disabled={!isManager} onChange={(e) => setAuto(e.target.checked)} /> Assign new leads automatically
      </label>
      <p className="mb-2 text-sm text-muted">Rules are checked in this order. If none applies, the rep with the fewest open leads gets it.</p>
      <ul className="mb-4 flex flex-col gap-2">
        {RULES.map(([k, label, hint]) => (
          <li key={k}><label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={rules[k]} disabled={!isManager || !auto} onChange={(e) => setRules({ ...rules, [k]: e.target.checked })} />
            <span><span className="font-medium">{label}</span><span className="block text-muted">{hint}</span></span>
          </label></li>
        ))}
      </ul>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField label="Most open leads per rep" type="number" min={1} max={500} value={cap} disabled={!isManager} onChange={(e) => setCap(e.target.value)} />
        <TextField label="Hot: respond within (min)" type="number" min={1} max={1440} value={hot} disabled={!isManager} onChange={(e) => setHot(e.target.value)} />
        <TextField label="Warm: respond within (min)" type="number" min={1} max={2880} value={warm} disabled={!isManager} onChange={(e) => setWarm(e.target.value)} />
      </div>
      <div className="mt-4">
        <SelectField label="NRI desk team" value={nri} disabled={!isManager} onChange={(e) => setNri(e.target.value)}>
          <option value="">No NRI desk</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </SelectField>
      </div>
      <p className="mt-3 text-xs text-muted">Cold leads must be answered by 10 am the next day. A missed time limit moves the lead to the next rep; two in a row send it to a manager.</p>
      {isManager && <div className="mt-4 flex items-center gap-3"><Button variant="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>{saved && <Notice>Saved.</Notice>}</div>}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </section>
  );
}

function MoneySettings() {
  const { firm, me, reload } = useAuth();
  const isOwner = me?.role === 'owner';
  const [gst, setGst] = useState(firm?.gst_registered ?? true);
  const [tds, setTds] = useState(String(firm?.default_tds_pct ?? 2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  return (
    <section className="mt-5 max-w-[640px] rounded-2xl border border-line bg-surface p-5">
      <h2 className="font-display text-lg">Commission</h2>
      <p className="mb-4 text-sm text-muted">Used for new bookings. Bookings already recorded keep the rates they were recorded with.</p>
      <label className="mb-3 flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={gst} disabled={!isOwner} onChange={(e) => setGst(e.target.checked)} /> The firm is GST-registered (add 18% GST to commission invoices)
      </label>
      <div className="max-w-[240px]"><TextField label="TDS builders usually deduct (%)" type="number" min={0} max={20} step="0.01" value={tds} disabled={!isOwner} onChange={(e) => setTds(e.target.value)} /></div>
      {isOwner ? (
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" disabled={busy} onClick={async () => {
            setBusy(true); setError(''); setSaved(false);
            try { await updateMoneySettings(gst, Number(tds)); await reload(); setSaved(true); } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
          }}>{busy ? 'Saving…' : 'Save'}</Button>
          {saved && <Notice>Saved.</Notice>}
        </div>
      ) : <p className="mt-3 text-xs text-muted">Only the owner can change these.</p>}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </section>
  );
}

function FirmDetails() {
  const { firm, me, reload } = useAuth();
  const isOwner = me?.role === 'owner';
  const [name, setName] = useState(firm?.name ?? '');
  const [city, setCity] = useState(firm?.city ?? '');
  const [rera, setRera] = useState(firm?.rera_number ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  return (
    <section className="mb-5 max-w-[640px] rounded-2xl border border-line bg-surface p-5">
      <h2 className="font-display text-lg">Firm details</h2>
      <p className="mb-4 text-sm text-muted">Shown on every post, WhatsApp message and branded brochure. The RERA agent number is required before anything can be sent to buyers.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Firm name" value={name} disabled={!isOwner} onChange={(e) => setName(e.target.value)} />
        <TextField label="City" value={city} disabled={!isOwner} onChange={(e) => setCity(e.target.value)} />
        <div className="sm:col-span-2"><TextField label="RERA agent number" value={rera} disabled={!isOwner} onChange={(e) => setRera(e.target.value)} placeholder="RC/HARERA/GGM/2026/0001"
          hint={firm?.rera_number ? undefined : 'Missing: posts and WhatsApp messages stay blocked until you add it.'} /></div>
      </div>
      {isOwner ? (
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" disabled={busy} onClick={async () => {
            setBusy(true); setError(''); setSaved(false);
            try { await updateFirmDetails(name, city, rera); await reload(); setSaved(true); } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
          }}>{busy ? 'Saving…' : 'Save'}</Button>
          {saved && <Notice>Saved. Click "Write again" on a project's sales brief to use the new details.</Notice>}
        </div>
      ) : <p className="mt-3 text-xs text-muted">Only the owner can change these.</p>}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </section>
  );
}
